import { renderToStaticMarkup, TReaderDocument } from '@app-evolution-cms/email-builder';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ComponentType,
  type JSX,
} from 'react';

import type { MediaPickerComponent } from '../editor/commands/button-image';
import { EditorEmail } from './editor-email';
import { normalizeBlockDocument } from './editor-email/utils/normalize-block-document';
import { EMPTY_LAYOUT } from './empty-layout';

export type EmailBuilderContent = {
  json: TReaderDocument;
  html: string;
};

export type EmailBuilderRef = {
  getContent: () => EmailBuilderContent;
};

export type EmailBuilderProps = {
  mediaPicker?: ComponentType<MediaPickerComponent>;
  isBlock?: boolean;
  emailBlocksElement?: JSX.Element;
  savedBlocks?: {
    id: string;
    name: string;
    description?: string;
    key?: string;
    item?: string | JSX.Element;
    document: TReaderDocument;
  }[];
  theme?: 'light' | 'dark';
  value?: TReaderDocument;
  onChange?: (nextDocument: TReaderDocument) => void;
  onSaveBlock?: (payload: {
    name: string;
    description: string;
    document: TReaderDocument;
    html: string;
  }) => void | Promise<void>;
};

/**
 * EmailBuilder
 * @callback `getContent` Available through `ref`. Returns the current document as `{ json, html }`.
 */
export const EmailBuilder = forwardRef<EmailBuilderRef, EmailBuilderProps>(function EmailBuilder(
  {
    isBlock = false,
    emailBlocksElement,
    mediaPicker,
    theme = 'dark',
    value = EMPTY_LAYOUT,
    onChange,
    savedBlocks = [],
    onSaveBlock,
  },
  ref,
) {
  // Without onChange there is no controlling parent, so the document lives in local state and is only read via ref.
  const isControlled = onChange != null;
  const normalizeDocument = useCallback(
    (nextDocument: TReaderDocument) =>
      isBlock ? normalizeBlockDocument(nextDocument, 'root') : nextDocument,
    [isBlock],
  );

  const [internalDocument, setInternalDocument] = useState<TReaderDocument>(() =>
    normalizeDocument(value),
  );
  // Tracks the `value` reference the internal state was last synced from, so an uncontrolled
  // `value` that only becomes available after mount (e.g. edit mode loads content asynchronously
  // via `form.reset()`) can still seed the document once it arrives.
  const syncedValueRef = useRef(value);

  useEffect(() => {
    if (isControlled || value === syncedValueRef.current) return;

    syncedValueRef.current = value;
    setInternalDocument(normalizeDocument(value));
  }, [isControlled, normalizeDocument, value]);

  const document = isControlled ? normalizeDocument(value) : internalDocument;

  const handleDocumentChange = useCallback(
    (nextDocument: TReaderDocument) => {
      const normalizedDocument = normalizeDocument(nextDocument);

      if (!isControlled) {
        setInternalDocument(normalizedDocument);
      }

      onChange?.(normalizedDocument);
    },
    [isControlled, normalizeDocument, onChange],
  );

  useEffect(() => {
    if (isControlled && document !== value) {
      onChange?.(document);
    }
  }, [document, isControlled, onChange, value]);

  useImperativeHandle(
    ref,
    () => ({
      getContent() {
        return {
          json: document,
          html: renderToStaticMarkup(document, { rootBlockId: 'root' }),
        };
      },
    }),
    [document],
  );

  return (
    <EditorEmail
      document={document}
      isBlock={isBlock}
      emailBlocksElement={emailBlocksElement}
      mediaPicker={mediaPicker}
      rootBlockId="root"
      theme={theme}
      onDocumentChange={handleDocumentChange}
      savedBlocks={savedBlocks}
      onSaveBlock={onSaveBlock}
    />
  );
});
